import * as React from 'react';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useTranslation } from 'react-i18next';
import { z } from 'zod';
import { api, ApiError } from '@client/lib/api';
import { setLocale } from '@client/lib/i18n';
import { Button } from '@client/components/ui/button';
import { Wordmark } from '@client/components/brand/logo';

export const Route = createFileRoute('/auth/verify')({
  validateSearch: z.object({ token: z.string().optional(), lang: z.string().optional(), share: z.string().optional() }),
  component: VerifyPage,
});

/** The link in the email lands here; a click (not a GET) exchanges the token, so mail scanners cannot consume it. */
function VerifyPage() {
  const { t } = useTranslation();
  const { token, share } = Route.useSearch();
  const navigate = useNavigate();
  const [state, setState] = React.useState<'idle' | 'working' | 'failed'>('idle');

  const go = async () => {
    if (!token) {
      setState('failed');
      return;
    }
    setState('working');
    try {
      const res = await api<{ ok: boolean; redirect_to: string; locale: 'en' | 'it' }>(share ? '/api/share/verify' : '/api/auth/verify', { body: { token } });
      await setLocale(res.locale);
      window.location.assign(res.redirect_to || '/');
    } catch (err) {
      if (err instanceof ApiError && err.status === 0) setState('idle');
      else setState('failed');
    }
  };

  return (
    <div className="flex min-h-dvh items-center justify-center bg-surface px-5">
      <div className="w-full max-w-[440px] rounded-panel border border-border bg-card p-8 shadow-panel">
        <Wordmark width={140} className="mb-8" />
        {state === 'failed' ? (
          <>
            <h1 className="page-title">{t('verify.failed')}</h1>
            <p className="mt-2 text-[16px] leading-6 text-body">{share ? t('share.linkUsed') : t('login.expired')}</p>
            <Button className="mt-6" onClick={() => (share ? navigate({ to: '/s/$slug', params: { slug: share } }) : navigate({ to: '/login', search: { error: 'expired' } }))}>
              {share ? t('share.backToDocument') : t('verify.requestNew')}
            </Button>
          </>
        ) : (
          <>
            <h1 className="page-title">{share ? t('share.confirmTitle') : t('verify.title')}</h1>
            <p className="mt-2 text-[16px] leading-6 text-body">{t('verify.body')}</p>
            <Button className="mt-6 w-full" size="lg" onClick={() => void go()} loading={state === 'working'}>
              {state === 'working' ? t('verify.working') : share ? t('share.confirmButton') : t('verify.button')}
            </Button>
          </>
        )}
      </div>
    </div>
  );
}
